import type { DeepPartial } from '@gonfalon/collections/deepMerge';
import { deepMerge } from '@gonfalon/collections/deepMerge';
import { type Draft, type Immutable, enableMapSet, produce } from 'immer';

// lets us update Set objects in mocks with methods like ".add()" to create immutable copies
enableMapSet();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RecipeCreator<T extends object> = (...args: any[]) => (obj: Draft<T>) => void;

// Re-export types used in the public signature of createMockResourceBuilder so
// that consumers (e.g. @gonfalon/openapi mocks) can reference them directly
// without triggering TS2742 "non-portable" errors from internal node_modules paths.
export type { DeepPartial } from '@gonfalon/collections/deepMerge';
export type { Draft } from 'immer';

/**
 * Creates a mock resource builder, given a function that return the "seed" or initial state of the resource,
 * plus an object of Immer "recipe" methods which will mutate the seed at creation time.
 *
 * Example:
 * ```ts
 * const mockResource = createMockResourceBuilder(
 *  () => ({ name: 'default-name', key: 'default-key' }),
 *  {
 *    withName: (name: string) => (draft) => {
 *      draft.name = name;
 *    },
 *    withKey: (key: string) => (draft) => {
 *      draft.key = key;
 *    },
 *    withKeyMatchingName: (name: string) => (draft) => {
 *      // can change multiple, nested properties in a single recipe
 *      draft.name = name;
 *      draft.key = name;
 *    },
 *  },
 * );
 *
 * const mockedResource = mockResource().withName('some other name').create(); // returns { name: 'some other name', key: 'default-key' }
 * ```
 */
export function createMockResourceBuilder<T extends object, R extends { [name: string]: RecipeCreator<T> }>(
  createSeed: () => T,
  recipeCreators?: R,
) {
  return () => {
    // tracks the mutating "recipes" to apply to the seed at creation time
    const recipesToApply: Array<(obj: Draft<T>) => void> = [];

    const builderAPI: {
      create: (seed?: DeepPartial<T>) => T;
    } & {
      [key in keyof R]: (...args: Parameters<R[key]>) => typeof builderAPI;
    } = {
      create(seed) {
        // apply each recipe to the seed in sequence and return the result
        return recipesToApply.reduce(
          (acc, recipe) => produce(acc as Immutable<Draft<T>>, recipe) as T,
          deepMerge(createSeed(), seed ?? {}),
        );
      },
      ...Object.entries(recipeCreators ?? {}).reduce(
        (acc, [name, createRecipe]) => {
          acc[name as keyof R] = (...args: Parameters<typeof createRecipe>) => {
            recipesToApply.push(createRecipe(...args));
            return builderAPI;
          };
          return acc;
        },
        {} as {
          [key in keyof R]: (...args: Parameters<R[key]>) => typeof builderAPI;
        },
      ),
    };

    return builderAPI;
  };
}
